import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/**
 * <coffret-composer> — tunnel « Compose ton coffret ».
 *
 * Étapes : capacity (2/3/5) → compose (grille + slots) → finish (emballage + message).
 * État persistant en localStorage (draft), survivant aux allers-retours éditeur.
 * Chaque paire devient une ligne de panier réelle (variante « Une paire », SKU
 * 1-CH-…) avec properties designId / _coffretId / _coffretRole / _coffretCapacity :
 * le format que produisent déjà les fiches produit, et que consomment l'atelier
 * et l'expédition sans modification.
 *
 * Mode « bf » (page Black Friday, section bf-pack-composer) : pas d'étape
 * capacité, 5 slots d'emblée, le pack se valide à 2, 3 ou 5 paires (barème
 * boosté par palier, config.discounts indexé sur le nombre RÉEL de paires), le
 * panneau latéral remplace la barre de slots, et les lignes portent
 * _coffretOffer (barème appliqué par le backend) + _coffretPage (retour depuis
 * le panier). Brouillon séparé (lfc-coffret-draft:bf).
 */

const DRAFT_KEY = 'lfc-coffret-draft:v1';
const SEED_KEY = 'lfc-coffret-seed';
const DRAFT_TTL_MS = 7 * 24 * 3600 * 1000;

class CoffretComposer extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-ref="composer-config"]').textContent);
    this.editorBase = this.dataset.editorBase;
    // Mode « direct » (ex-« bf ») : 5 slots d'emblée, pack valide à 2/3/5,
    // coffret proposé en popup — page Black Friday ET « Compose ton pack ».
    // Même brouillon pour les deux pages (c'est le même pack en cours).
    this.bf = this.dataset.mode === 'direct' || this.dataset.mode === 'bf';
    this.draftKey = this.bf ? `${DRAFT_KEY.split(':')[0]}:direct` : DRAFT_KEY;
    this.validCounts = this.bf ? (this.config.validCounts || [2, 3, 5]) : null;
    this.maxSlots = this.bf ? (this.config.maxSlots || 5) : null;

    this.refs = {};
    for (const element of this.querySelectorAll('[data-ref]')) {
      this.refs[element.dataset.ref] = element;
    }

    // Popup coffret du mode bf : même mécanique dialog-component que la popup
    // taille (data-ref="popup-modal" pour popup.js), repérée par son attribut dédié
    const packagingDialog = this.querySelector('[data-packaging-dialog]');
    if (packagingDialog) this.refs['packaging-dialog'] = packagingDialog;

    this.cards = new Map();
    for (const card of this.querySelectorAll('[data-ref="coffret-card"]')) {
      const data = JSON.parse(card.querySelector('[data-ref="coffret-card-data"]').textContent);
      this.cards.set(String(data.productId), { element: card, data });
    }

    // Idées de pack (métaobjets pack_idea) : données produit au même format
    // que les cartes, pour remplir les slots d'un coup
    this.ideas = new Map();
    for (const idea of this.querySelectorAll('[data-ref="pack-idea"]')) {
      const data = JSON.parse(idea.querySelector('[data-ref="pack-idea-data"]').textContent);
      this.ideas.set(String(data.handle), data);
    }

    this.draft = this.#loadDraft();
    this.#consumeSeed();
    this.#bind();
    // Mode direct : pas d'étape capacité, la paire venue de la fiche produit
    // est ajoutée tout de suite au pack en cours
    if (this.bf) this.#applySeed();
    this.#consumeEditorReturn();
    this.#render();
  }

  // ——————————————————————————————————————————————— état ———

  #loadDraft() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.draftKey) || 'null');
      if (raw && raw.version === 1 && Date.now() - raw.updatedAt < DRAFT_TTL_MS) {
        if (this.bf) {
          // Brouillon venu du panier (capacité = paires du pack) ou d'une
          // visite précédente : toujours 5 slots, jamais l'étape capacité.
          raw.capacity = this.maxSlots;
          // Toujours à l'étape composition : les anciennes étapes (capacité,
          // emballage en pleine page) n'existent pas sur cette page
          raw.step = 'compose';
          raw.slots = (raw.slots || []).slice(0, this.maxSlots);
        }
        return raw;
      }
    } catch {
      /* localStorage indisponible ou draft corrompu */
    }
    return this.#freshDraft();
  }

  #freshDraft() {
    return {
      version: 1,
      coffretId: crypto.randomUUID(),
      step: this.bf ? 'compose' : 'capacity',
      capacity: this.bf ? this.maxSlots : null,
      slots: [],
      pending: null,
      packaging: { type: null, variantId: null, price: 0 },
      updatedAt: Date.now(),
    };
  }

  #save() {
    this.draft.updatedAt = Date.now();
    try {
      localStorage.setItem(this.draftKey, JSON.stringify(this.draft));
    } catch {
      /* mode privé strict : le tunnel marche en mémoire */
    }
  }

  /** Paire en cours sur une fiche produit → slot 1 prérempli. */
  #consumeSeed() {
    let seed = null;
    try {
      seed = JSON.parse(localStorage.getItem(SEED_KEY) || 'null');
      localStorage.removeItem(SEED_KEY);
    } catch {
      return;
    }
    if (!seed?.productId || !this.cards.has(String(seed.productId))) return;

    // Mode direct : on complète simplement le pack en cours (capacité fixe)
    if (this.bf) {
      this.seed = seed;
      return;
    }

    const hasProgress = this.draft.slots.length > 0 || this.draft.capacity;
    if (hasProgress) {
      const resume = confirm(
        'Tu as déjà un pack en cours. OK pour le reprendre, Annuler pour recommencer avec cette paire.',
      );
      if (resume) return;
      this.draft = this.#freshDraft();
    }

    this.seed = seed;
  }

  /** Retour de l'éditeur de personnalisation (?d=…&thumb=…). */
  #consumeEditorReturn() {
    const params = new URLSearchParams(window.location.search);
    const designId = params.get('d');
    const thumb = params.get('thumb');

    if (params.has('d') || params.has('thumb')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('d');
      url.searchParams.delete('thumb');
      history.replaceState({}, '', url.toString());
    }

    if (!designId || !this.draft.pending) return;

    this.draft.pending.designId = designId;
    if (thumb) this.draft.pending.thumb = thumb;
    this.#save();

    const card = this.cards.get(String(this.draft.pending.productId));
    if (card) {
      requestAnimationFrame(() =>
        this.#openPopup(card.data, {
          designId,
          thumb: this.draft.pending.thumb,
          slotId: this.draft.pending.slotId,
        }),
      );
    }
  }

  // ——————————————————————————————————————————————— listeners ———

  #bind() {
    this.querySelectorAll('[data-ref="capacity-choice"]').forEach((button) =>
      button.addEventListener('click', () => this.#chooseCapacity(parseInt(button.dataset.capacity, 10))),
    );

    this.refs['capacity-edit']?.addEventListener('click', () => {
      this.draft.step = 'capacity';
      this.#save();
      this.#render();
    });

    this.refs['sport-filters']?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sport-filter]');
      if (!button) return;
      this.querySelectorAll('[data-sport-filter]').forEach((b) => b.classList.toggle('is-active', b === button));
      this.#applyGridFilters();
    });

    this.refs['product-search']?.addEventListener('input', () => this.#applyGridFilters());

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ref="pack-idea-add"]');
      if (!button) return;
      const card = button.closest('[data-ref="pack-idea"]');
      const idea = card && this.ideas.get(String(card.dataset.ideaHandle));
      if (idea) this.#openIdeaPopup(idea);
    });

    this.refs['products-grid']?.addEventListener('click', (event) => {
      const card = event.target.closest('[data-ref="coffret-card"]');
      if (!card) return;
      const entry = this.cards.get(card.dataset.productId);
      if (!entry) return;

      if (event.target.closest('[data-ref="coffret-card-personalize"]')) this.#startPersonalization(entry.data);
      if (event.target.closest('[data-ref="coffret-card-add"]')) this.#openPopup(entry.data, {});
    });

    this.refs['slots-continue']?.addEventListener('click', () => this.#continue());

    this.refs['slots-back']?.addEventListener('click', () => {
      this.draft.step = 'compose';
      this.#save();
      this.#render();
    });

    this.refs['slots-list']?.addEventListener('click', (event) => {
      const slotElement = event.target.closest('[data-slot-id]');
      if (!slotElement) return;
      const slotId = slotElement.dataset.slotId;

      if (event.target.closest('.coffret-slot__remove')) return this.#removeSlot(slotId);
      if (event.target.closest('.coffret-slot__duplicate')) return this.#duplicateSlot(slotId);
      if (slotElement.classList.contains('coffret-slot--pending')) return this.#reopenPending();
    });

    this.refs['packaging-choices']?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-packaging-type]');
      if (!button) return;
      this.draft.packaging = {
        type: button.dataset.packagingType,
        variantId: button.dataset.variantId ? parseInt(button.dataset.variantId, 10) : null,
        price: parseInt(button.dataset.price || '0', 10),
      };
      this.#save();
      this.#render();
    });

    this.refs['add-coffret']?.addEventListener('click', () => this.#addToCart());

    this.refs['packaging-dialog']
      ?.querySelector('.dialog-modal__content')
      ?.addEventListener('click', (event) => {
        if (event.clientX === 0 && event.clientY === 0) event.stopPropagation();
      });

    // Popup : garde contre les clics synthétiques (0,0) des labels — le handler
    // backdrop de popup.js les prendrait pour un clic hors dialog (fermeture)
    this.refs['popup-modal']
      ?.querySelector('.dialog-modal__content')
      ?.addEventListener('click', (event) => {
        if (event.clientX === 0 && event.clientY === 0) event.stopPropagation();
      });

    this.refs['popup-confirm']?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#confirmPopup();
    });

    this.refs['popup-fields']?.addEventListener('change', () => this.#updatePopupState());
    this.refs['popup-modal']?.addEventListener('close', () => {
      this.ideaContext = null;
    });
  }

  /** Clic « Ajouter au panier » / « Continuer » : ajout direct, popup coffret (3 paires) ou étape emballage. */
  #continue() {
    if (this.bf && !this.#packIsValid()) return;
    // 2 et 5 paires : pas d'étape emballage → ajout direct au panier.
    if (this.#packSize() !== 3) {
      this.#addToCart();
      return;
    }
    // Mode direct : le coffret est PROPOSÉ au moment d'ajouter (popup), on ne
    // change pas d'étape et le bouton ne dit jamais « Continuer ».
    if (this.bf) {
      this.#openPackagingDialog();
      return;
    }
    this.draft.step = 'finish';
    this.#save();
    this.#render();
    window.scrollTo({ top: 0 });
  }

  // ——————————————————————————————————————————————— idées de pack ———

  /**
   * Idée de pack : une seule taille demandée (commune aux paires), gamme =
   * première gamme visible de chaque paire, puis slots remplis d'un coup et
   * ajout enchaîné (popup coffret à 3 paires). Un pack en cours est remplacé
   * après confirmation.
   */
  #openIdeaPopup(idea) {
    const sizes = idea.products
      .map((product) => (product.tailleOption ? product.tailleValues : null))
      .filter(Boolean);
    const common = sizes.length ? sizes.reduce((acc, values) => acc.filter((v) => values.includes(v))) : [];
    if (sizes.length && !common.length) {
      alert('Les paires de ce pack n\'ont pas de taille commune : compose-le paire par paire.');
      return;
    }
    // Gamme (ex. running : Confort / Race) : valeurs communes aux paires qui
    // ont l'option ; un seul choix pour tout le pack, proposé s'il y en a 2+
    const withGamme = idea.products.filter((product) => product.gammeOption && product.gammeValues.length);
    const gammes = withGamme.length
      ? withGamme.map((product) => product.gammeValues).reduce((acc, values) => acc.filter((v) => values.includes(v)))
      : [];
    this.ideaContext = { idea, sizes: common, gammes: gammes.length > 1 ? gammes : [] };

    this.refs['popup-title'].textContent = idea.name;
    this.refs['popup-thumbs'].innerHTML = idea.products
      .filter((product) => product.image)
      .map((product) => `<img src="${this.#escape(product.image)}" alt="${this.#escape(product.title)}">`)
      .join('');
    let html = `<p class="perso-popup__lead">${idea.count} paires à la même taille. Tu pourras encore modifier ton pack avant de valider.</p>`;
    if (common.length) {
      html += '<fieldset class="perso-popup__fieldset"><legend class="perso-popup__legend">Taille</legend><div class="variant-picker__option-values">';
      common.forEach((value, index) => {
        html += `<div class="variant-picker__option-value"><input type="radio" name="coffret-idea-taille" id="coffret-idea-taille-${index}" value="${this.#escape(value)}"><label for="coffret-idea-taille-${index}">${this.#escape(value)}</label></div>`;
      });
      html += '</div></fieldset>';
    }
    if (this.ideaContext.gammes.length) {
      const meta = withGamme[0].gammes || {};
      html += `<fieldset class="perso-popup__fieldset"><legend class="perso-popup__legend">${this.#escape(withGamme[0].gammeOption)}</legend><div class="perso-gamme-grid">`;
      this.ideaContext.gammes.forEach((value) => {
        const info = meta[value] || { title: value, description: '', iconHtml: '' };
        html += `<label class="perso-gamme-card perso-gamme-card--selectable">
          <input type="radio" name="coffret-idea-gamme" value="${this.#escape(value)}">
          <span class="perso-gamme-card__icon">${info.iconHtml || ''}</span>
          <span class="perso-gamme-card__body">
            <span class="perso-gamme-card__title">${info.titleHtml || this.#escape(info.title || value)}</span>
            ${info.description ? `<span class="perso-gamme-card__description">${info.description}</span>` : ''}
            <span class="perso-gamme-card__price" data-idea-gamme-price="${this.#escape(value)}"></span>
          </span>
        </label>`;
      });
      html += '</div></fieldset>';
    }
    this.refs['popup-fields'].innerHTML = html;
    this.popupContext = null;
    this.#updateIdeaPrices();
    this.refs['popup-confirm'].disabled = common.length > 0 || this.ideaContext.gammes.length > 0;

    const dialog = this.refs['popup-modal'];
    if (!dialog.open) {
      try {
        dialog.showModal();
        document.body.classList.add('overflow-hidden');
      } catch {
        /* déjà ouvert */
      }
    }
    dialog.scrollTop = 0;
  }

  /** Prix du pack par gamme (toutes paires, à la taille choisie ou la première) : total pour la 1ʳᵉ, delta pour les autres. */
  #updateIdeaPrices() {
    const { idea, sizes, gammes } = this.ideaContext || {};
    if (!idea || !gammes?.length) return;
    const taille = sizes.length ? this.#popupValue('coffret-idea-taille') || sizes[0] : null;
    const totalFor = (gamme) =>
      idea.products.reduce((sum, product) => {
        const variant = this.#findPairVariant(product, product.tailleOption ? taille : null, this.#ideaGammeFor(product, gamme));
        return sum + (variant ? variant.price : 0);
      }, 0);
    const base = totalFor(gammes[0]);
    gammes.forEach((gamme) => {
      const element = this.refs['popup-fields'].querySelector(`[data-idea-gamme-price="${CSS.escape(gamme)}"]`);
      if (!element) return;
      const delta = totalFor(gamme) - base;
      element.textContent = delta > 0 ? `+ ${formatMoney(delta)}` : formatMoney(totalFor(gamme));
    });
  }

  /** Gamme effective d'une paire pour la gamme choisie sur l'idée (repli : sa première gamme). */
  #ideaGammeFor(product, chosen) {
    if (!product.gammeOption) return null;
    if (chosen && product.gammeValues.includes(chosen)) return chosen;
    return product.gammeValues[0] || null;
  }

  #confirmIdea() {
    const { idea, sizes, gammes } = this.ideaContext || {};
    if (!idea) return false;
    const taille = sizes.length ? this.#popupValue('coffret-idea-taille') : null;
    if (sizes.length && !taille) return false;
    const chosenGamme = gammes.length ? this.#popupValue('coffret-idea-gamme') : null;
    if (gammes.length && !chosenGamme) return false;

    if (this.draft.slots.length && !confirm('Ton pack en cours sera remplacé par cette idée de pack. Continuer ?')) {
      return false;
    }
    const slots = [];
    for (const product of idea.products) {
      const gamme = this.#ideaGammeFor(product, chosenGamme);
      const variant = this.#findPairVariant(product, product.tailleOption ? taille : null, gamme);
      if (!variant || variant.available === false) {
        alert(`« ${product.title} » n'est pas disponible en ${taille || 'cette version'} : compose ton pack paire par paire.`);
        return false;
      }
      slots.push({
        slotId: crypto.randomUUID(),
        productId: product.productId,
        title: product.title,
        image: product.image,
        variantId: variant.id,
        sku: variant.sku,
        price: variant.price,
        taille: product.tailleOption ? taille : null,
        gamme,
        designId: null,
        thumb: null,
      });
    }
    this.draft.slots = slots.slice(0, this.draft.capacity);
    this.draft.pending = null;
    this.draft.packaging = { type: null, variantId: null, price: 0 };
    this.#save();
    this.ideaContext = null;
    this.#render();
    // Enchaîne l'ajout : popup coffret à 3 paires, ajout direct sinon
    requestAnimationFrame(() => this.#continue());
    return true;
  }

  // ——————————————————————————————————————————————— capacité & slots ———

  /** Taille du pack : la capacité choisie, ou en mode bf le nombre réel de paires. */
  #packSize() {
    return this.bf ? this.draft.slots.length : this.draft.capacity;
  }

  #packIsValid() {
    return this.bf ? this.validCounts.includes(this.draft.slots.length) : this.draft.slots.length === this.draft.capacity;
  }

  /** Pourcentage du barème pour la taille courante (0 si le pack n'est pas valide). */
  #discountPercent() {
    const size = this.#packSize();
    const direct = this.config.discounts[String(size)];
    if (direct || !this.bf) return direct || 0;
    // Mode direct, nombre non valide (4 paires) : on garde la remise du
    // palier atteint juste en dessous (3 paires) pour que le prix affiché ne
    // remonte pas d'un coup ; le pack reste bloqué tant qu'il manque une paire.
    const reached = this.validCounts.filter((n) => n < size).pop();
    return reached ? this.config.discounts[String(reached)] || 0 : 0;
  }

  /** Filtre la grille : sport actif × recherche par nom (insensible aux accents). */
  #applyGridFilters() {
    const normalize = (text) =>
      String(text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');

    const query = normalize(this.refs['product-search']?.value).trim();
    const active = this.querySelector('[data-sport-filter].is-active')?.dataset.sportFilter;

    // Un onglet = une grille-collection (triée bestsellers par le cron) :
    // on bascule la grille visible, la recherche filtre dans la grille active.
    for (const grid of this.querySelectorAll('[data-collection-grid]')) {
      const isActive = grid.dataset.collectionGrid === active;
      grid.hidden = !isActive;
      if (!isActive) continue;
      for (const card of grid.querySelectorAll('[data-ref="coffret-card"]')) {
        const entry = this.cards.get(String(card.dataset.productId));
        const title = entry?.data?.title || card.textContent || '';
        card.hidden = Boolean(query) && !normalize(title).includes(query);
      }
      for (const card of grid.querySelectorAll('[data-ref="pack-idea"]')) {
        card.hidden = Boolean(query) && !normalize(card.dataset.ideaName).includes(query);
      }
    }
  }

  #chooseCapacity(capacity) {
    if (this.draft.slots.length > capacity) {
      const keep = confirm(
        `Ton pack contient déjà ${this.draft.slots.length} paires : les dernières seront retirées. Continuer ?`,
      );
      if (!keep) return;
      this.draft.slots = this.draft.slots.slice(0, capacity);
    }
    this.draft.capacity = capacity;
    // Emballage : seul le coffret 3 paires existe physiquement — pour 2 et 5,
    // pas de choix, la commande part en emballage standard.
    this.draft.packaging = capacity === 3
      ? { type: null, variantId: null, price: 0 }
      : { type: 'none', variantId: null, price: 0 };
    this.draft.step = 'compose';
    this.#save();

    this.#applySeed();
    this.#render();
  }

  /** Paire venue de la fiche produit (seed) → slot rempli, ou popup si un choix manque. */
  #applySeed() {
    if (!this.seed) return;
    const seed = this.seed;
    this.seed = null;
    const entry = this.cards.get(String(seed.productId));
    if (!entry) return;
    if (this.draft.slots.length >= (this.draft.capacity || this.maxSlots)) {
      alert('Ton pack est déjà complet : retire une paire pour ajouter celle-ci.');
      return;
    }
    if (seed.taille && (entry.data.gammeOption ? seed.gamme : true)) {
      this.#fillSlot(null, entry.data, {
        taille: seed.taille,
        gamme: seed.gamme,
        designId: seed.designId,
        thumb: seed.thumb,
      });
    } else {
      this.#openPopup(entry.data, { designId: seed.designId, thumb: seed.thumb });
    }
  }

  #removeSlot(slotId) {
    this.draft.slots = this.draft.slots.filter((slot) => slot.slotId !== slotId);
    this.#save();
    this.#render();
  }

  #duplicateSlot(slotId) {
    if (this.draft.slots.length >= this.draft.capacity) return;
    const source = this.draft.slots.find((slot) => slot.slotId === slotId);
    const entry = source && this.cards.get(String(source.productId));
    if (!entry) return;

    this.#openPopup(entry.data, {
      designId: source.designId,
      thumb: source.thumb,
      preselect: { taille: source.taille, gamme: source.gamme },
    });
  }

  #reopenPending() {
    const pending = this.draft.pending;
    const entry = pending && this.cards.get(String(pending.productId));
    if (!entry) return;
    this.#openPopup(entry.data, { designId: pending.designId, thumb: pending.thumb, slotId: pending.slotId });
  }

  #startPersonalization(productData) {
    if (this.draft.slots.length >= this.draft.capacity) return;
    this.draft.pending = { slotId: crypto.randomUUID(), productId: productData.productId, templateId: productData.templateId };
    this.#save();

    const origin = encodeURIComponent(window.location.origin + window.location.pathname);
    window.location.href = `${this.editorBase}/use/${productData.templateId}?origin=${origin}`;
  }

  /** Variante « Une paire » correspondant aux choix (SKU 1-CH-…). */
  #findPairVariant(productData, taille, gamme) {
    return productData.variants.find((variant) => {
      if (!/^1-CH-/.test(variant.sku || '')) return false;
      if (productData.tailleOption && taille && !variant.options.includes(taille)) return false;
      if (productData.gammeOption && gamme && !variant.options.includes(gamme)) return false;
      return true;
    });
  }

  #fillSlot(slotId, productData, { taille, gamme, designId, thumb }) {
    const variant = this.#findPairVariant(productData, taille, gamme);
    if (!variant) return false;

    const slot = {
      slotId: slotId || crypto.randomUUID(),
      productId: productData.productId,
      title: productData.title,
      image: productData.image,
      variantId: variant.id,
      sku: variant.sku,
      price: variant.price,
      taille: taille || null,
      gamme: gamme || null,
      designId: designId || null,
      thumb: thumb || null,
    };

    const existingIndex = this.draft.slots.findIndex((s) => s.slotId === slot.slotId);
    if (existingIndex >= 0) this.draft.slots[existingIndex] = slot;
    else if (this.draft.slots.length < this.draft.capacity) this.draft.slots.push(slot);
    else return false;

    if (this.draft.pending?.slotId === slot.slotId || this.draft.pending?.productId === slot.productId) {
      this.draft.pending = null;
    }
    this.#save();
    return true;
  }

  // ——————————————————————————————————————————————— popup taille/gamme ———

  #openPopup(productData, { slotId, designId, thumb, preselect } = {}) {
    this.popupContext = { productData, slotId: slotId || null, designId: designId || null, thumb: thumb || null };

    this.refs['popup-title'].textContent = productData.title;
    this.refs['popup-thumbs'].innerHTML = '';
    const imageSrc = thumb || productData.image;
    if (imageSrc) {
      const img = document.createElement('img');
      img.src = imageSrc;
      img.alt = productData.title;
      this.refs['popup-thumbs'].appendChild(img);
    }

    // Tailles dépendantes de la gamme (ex. Carte Blanche : Multisport en
    // 30-35/36-40…, Cyclisme en 35-38/39-42…) : même règle que la fiche
    // produit (personalization.js) : gamme demandée en premier, titre de la
    // carte = valeur d'option (« Running », pas le titre marketing « Confort »),
    // picto du sport, et seules les tailles de la gamme choisie sont proposées,
    // triées de S à XL.
    const taillesByGamme = this.#taillesByGamme(productData);
    const gammeDependent = this.#isGammeDependent(productData, taillesByGamme);
    const rank = { S: 0, M: 1, L: 2, XL: 3 };
    const tailleValues = gammeDependent
      ? [...productData.tailleValues].sort((a, b) => (rank[String(a).split(' ')[0]] ?? 99) - (rank[String(b).split(' ')[0]] ?? 99))
      : productData.tailleValues;

    let tailleHtml = '';
    if (productData.tailleOption) {
      tailleHtml += `<fieldset class="perso-popup__fieldset"><legend class="perso-popup__legend">${productData.tailleOption}</legend><div class="variant-picker__option-values">`;
      tailleValues.forEach((value, index) => {
        const id = `coffret-taille-${index}`;
        const checked = preselect?.taille === value ? 'checked' : '';
        tailleHtml += `<div class="variant-picker__option-value"><input type="radio" name="coffret-taille" id="${id}" value="${this.#escape(value)}" ${checked}><label for="${id}">${value}</label></div>`;
      });
      tailleHtml += '</div></fieldset>';
    }
    let gammeHtml = '';
    if (productData.gammeOption && productData.gammeValues.length === 1) {
      // Une seule gamme visible (les gammes en rupture sont filtrées à la
      // source) : pas de choix à montrer, la valeur reste dans le formulaire
      gammeHtml += `<input type="radio" name="coffret-gamme" value="${this.#escape(productData.gammeValues[0])}" checked hidden>`;
    }
    if (productData.gammeOption && productData.gammeValues.length > 1) {
      gammeHtml += `<fieldset class="perso-popup__fieldset"><legend class="perso-popup__legend">${productData.gammeOption}</legend><div class="perso-gamme-grid">`;
      productData.gammeValues.forEach((value) => {
        const meta = productData.gammes[value] || { title: value, description: '', iconHtml: '' };
        const checked = preselect?.gamme === value ? 'checked' : '';
        const title = gammeDependent ? this.#escape(value) : (meta.titleHtml || meta.title);
        const iconHtml = gammeDependent ? this.#gammeIconForValue(meta.iconHtml || '', value) : (meta.iconHtml || '');
        gammeHtml += `<label class="perso-gamme-card perso-gamme-card--selectable">
          <input type="radio" name="coffret-gamme" value="${this.#escape(value)}" ${checked}>
          <span class="perso-gamme-card__icon">${iconHtml}</span>
          <span class="perso-gamme-card__body">
            <span class="perso-gamme-card__title">${title}</span>
            ${meta.description ? `<span class="perso-gamme-card__description">${meta.description}</span>` : ''}
            <span class="perso-gamme-card__price" data-gamme-price="${this.#escape(value)}"></span>
          </span>
        </label>`;
      });
      gammeHtml += '</div></fieldset>';
    }
    this.refs['popup-fields'].innerHTML = gammeDependent ? gammeHtml + tailleHtml : tailleHtml + gammeHtml;
    this.popupContext.gammeDependent = gammeDependent;
    this.popupContext.taillesByGamme = taillesByGamme;

    this.#updatePopupState();

    const dialog = this.refs['popup-modal'];
    if (!dialog.open) {
      try {
        dialog.showModal();
        document.body.classList.add('overflow-hidden');
      } catch {
        /* déjà ouvert */
      }
    }
    dialog.scrollTop = 0;
  }

  #popupValue(name) {
    return this.refs['popup-fields'].querySelector(`input[name="${name}"]:checked`)?.value ?? null;
  }

  /** Tailles disponibles par gamme (variantes existantes), pour les produits où elles diffèrent. */
  #taillesByGamme(productData) {
    const map = {};
    if (!productData.gammeOption || !productData.tailleOption) return map;
    for (const gamme of productData.gammeValues) {
      map[gamme] = new Set(
        (productData.variants ?? [])
          .filter((variant) => (variant.options ?? []).includes(gamme))
          .map((variant) => productData.tailleValues.find((t) => variant.options.includes(t)))
          .filter(Boolean),
      );
    }
    return map;
  }

  #isGammeDependent(productData, taillesByGamme) {
    if (!productData.gammeOption || !productData.tailleOption || productData.gammeValues.length < 2) return false;
    const signatures = productData.gammeValues.map((g) => [...(taillesByGamme[g] ?? [])].sort().join('|'));
    return signatures.some((signature) => signature !== signatures[0]);
  }

  /** Picto du sport pour une carte gamme en mode gamme-dépendante (coureur pour running). */
  #gammeIconForValue(iconHtml, value) {
    const overrides = { running: 'directions_run' };
    const target = overrides[String(value).toLowerCase()];
    const current = iconHtml.match(/data-icon-name="([^"]+)"/)?.[1];
    if (!target || !current || current === target) return iconHtml;
    return iconHtml
      .replaceAll(`icon--${current}`, `icon--${target}`)
      .replaceAll(`data-icon-name="${current}"`, `data-icon-name="${target}"`)
      .replaceAll(`/${current}/`, `/${target}/`);
  }

  /** Mode gamme-dépendante : bloc Taille masqué tant qu'aucune gamme n'est cochée, puis seules les tailles de la gamme choisie. */
  #filterPopupTailles() {
    const { productData, gammeDependent, taillesByGamme } = this.popupContext || {};
    if (!productData || !gammeDependent) return;
    const gamme = this.#popupValue('coffret-gamme');
    const tailleFieldset = this.refs['popup-fields'].querySelector('input[name="coffret-taille"]')?.closest('fieldset');
    if (tailleFieldset) tailleFieldset.hidden = !gamme;
    const allowed = gamme ? taillesByGamme[gamme] : new Set();
    for (const input of this.refs['popup-fields'].querySelectorAll('input[name="coffret-taille"]')) {
      const visible = allowed.has(input.value);
      const wrapper = input.closest('.variant-picker__option-value');
      if (wrapper) wrapper.hidden = !visible;
      if (!visible && input.checked) input.checked = false;
    }
  }

  #updatePopupState() {
    if (this.ideaContext) {
      this.#updateIdeaPrices();
      const { sizes, gammes } = this.ideaContext;
      this.refs['popup-confirm'].disabled =
        (sizes.length > 0 && !this.#popupValue('coffret-idea-taille')) || (gammes.length > 0 && !this.#popupValue('coffret-idea-gamme'));
      return;
    }
    const { productData } = this.popupContext || {};
    if (!productData) return;

    this.#filterPopupTailles();
    const taille = this.#popupValue('coffret-taille');
    const gamme = this.#popupValue('coffret-gamme');

    // Prix par gamme (delta vs première gamme, à taille constante)
    if (productData.gammeOption) {
      const base = this.#findPairVariant(productData, taille, productData.gammeValues[0]);
      productData.gammeValues.forEach((value) => {
        const element = this.refs['popup-fields'].querySelector(`[data-gamme-price="${CSS.escape(value)}"]`);
        const variant = this.#findPairVariant(productData, taille, value);
        if (!element || !variant) return;
        const delta = base ? variant.price - base.price : 0;
        element.textContent = delta > 0 ? `+ ${formatMoney(delta)}` : formatMoney(variant.price);
      });
    }

    const needsTaille = Boolean(productData.tailleOption && productData.tailleValues.length);
    const needsGamme = Boolean(productData.gammeOption && productData.gammeValues.length);
    this.refs['popup-confirm'].disabled = (needsTaille && !taille) || (needsGamme && !gamme);
  }

  #confirmPopup() {
    if (this.ideaContext) {
      if (this.#confirmIdea()) this.refs['popup-modal'].closest('dialog-component')?.close();
      return;
    }
    const { productData, slotId, designId, thumb } = this.popupContext || {};
    if (!productData) return;

    const filled = this.#fillSlot(slotId, productData, {
      taille: this.#popupValue('coffret-taille'),
      gamme: this.#popupValue('coffret-gamme'),
      designId,
      thumb,
    });

    if (filled) {
      this.popupContext = null;
      // La popup taille n'est plus forcément le premier dialog-component de
      // la page (popup coffret sur la page Black Friday) : on ferme la sienne.
      this.refs['popup-modal'].closest('dialog-component')?.close();
      this.#render();
    }
  }

  // ——————————————————————————————————————————————— rendu ———

  #render() {
    const step = this.draft.capacity ? this.draft.step : 'capacity';
    this.dataset.step = step;

    this.#renderSlots(step);
    if (this.bf) this.#renderAside(step);
    if (step === 'finish' || this.#packagingDialogOpen()) {
      this.#renderPackaging();
      this.#renderSummary();
    }
  }

  #packagingDialogOpen() {
    return Boolean(this.bf && this.refs['packaging-dialog']?.open);
  }

  /** Mode bf, 3 paires : proposition du coffret dans une popup (emballage + récap + ajout). */
  #openPackagingDialog() {
    const dialog = this.refs['packaging-dialog'];
    if (!dialog) return this.#addToCart();
    if (!this.draft.packaging.type) this.draft.packaging = { type: 'none', variantId: null, price: 0 };
    this.#renderPackaging();
    this.#renderSummary();
    this.refs['composer-error'].hidden = true;
    if (!dialog.open) {
      try {
        dialog.showModal();
        document.body.classList.add('overflow-hidden');
      } catch {
        /* déjà ouvert */
      }
    }
    dialog.scrollTop = 0;
  }

  /**
   * Mode bf : panneau latéral (paliers, décompte, total, aide). Les slots et
   * le bouton sont rendus par #renderSlots sur les mêmes data-ref.
   */
  #renderAside(step) {
    const count = this.draft.slots.length;
    const percent = this.#discountPercent();
    const pairsTotal = this.draft.slots.reduce((sum, slot) => sum + slot.price, 0);
    const discount = Math.round((pairsTotal * percent) / 100);
    const packagingPrice = this.draft.packaging.type && this.draft.packaging.type !== 'none' ? this.draft.packaging.price : 0;
    const total = pairsTotal - discount + packagingPrice;

    // Paliers : atteint = pack valide à ce nombre ; courant = le palier du pack
    for (const tier of this.querySelectorAll('[data-tier]')) {
      const size = parseInt(tier.dataset.tier, 10);
      tier.classList.toggle('is-reached', count >= size);
      tier.classList.toggle('is-current', this.#packIsValid() && size === count);
    }

    const hint = this.refs['bf-hint'];
    if (hint) {
      const pct = (size) => this.config.discounts[String(size)] || 0;
      let text;
      if (count === 0) text = 'Ajoute 2, 3 ou 5 paires à ton pack';
      else if (count === 1) text = `Il manque une paire pour débloquer -${pct(2)} %`;
      else if (count === 4) text = `-${percent} % appliqués · il manque une paire pour la totale (-${pct(5)} %), ou retires-en une`;
      else if (count === 2) text = `Pack duo validé (-${percent} %) · une paire de plus = -${pct(3)} %`;
      else if (count === 3) text = `Pack validé (-${percent} %) · 5 paires = -${pct(5)} %`;
      else text = `La totale : -${percent} % sur tout le pack`;
      hint.textContent = text;
      hint.classList.toggle('is-ready', this.#packIsValid());
    }

    const totals = this.refs['bf-total'];
    if (totals) {
      totals.innerHTML = count
        ? `<span class="bf-aside__price${discount ? ' bf-aside__price--struck' : ''}">${formatMoney(pairsTotal + packagingPrice)}</span>${discount ? `<span class="bf-aside__price bf-aside__price--final">${formatMoney(total)}</span>` : ''}`
        : '';
    }
    // Bouton : jamais « Continuer ». À 1 ou 4 paires il est grisé et dit
    // « Plus qu'une paire » ; valide → « Ajouter au panier » + total.
    const cta = this.refs['slots-continue'];
    if (cta && step === 'compose') {
      if (count === 1 || count === 4) cta.innerHTML = '<span>Plus qu\'une paire</span>';
      else cta.innerHTML = `<span>Ajouter au panier</span>${count ? `<span class="bf-aside__cta-price">${formatMoney(total)}</span>` : ''}`;
    }
  }

  #renderSlots(step) {
    const bar = this.refs['slots-bar'];
    if (!bar) return;
    bar.hidden = step === 'capacity';
    if (bar.hidden) return;

    const list = this.refs['slots-list'];
    list.innerHTML = '';

    for (let index = 0; index < (this.draft.capacity || 0); index++) {
      const slot = this.draft.slots[index];
      const element = document.createElement('div');
      element.className = 'coffret-slot';

      if (slot) {
        element.classList.add('coffret-slot--filled');
        element.dataset.slotId = slot.slotId;
        element.innerHTML = `
          <img class="coffret-slot__img" src="${this.#escape(slot.thumb || slot.image || '')}" alt="${this.#escape(slot.title)}">
          <span class="coffret-slot__meta">${this.#slotMetaHtml(slot)}</span>
          <button type="button" class="coffret-slot__remove" aria-label="Supprimer">×</button>
          ${this.draft.slots.length < this.draft.capacity ? '<button type="button" class="coffret-slot__duplicate" aria-label="Dupliquer">⧉</button>' : ''}
        `;
      } else if (index === this.draft.slots.length && this.draft.pending?.designId) {
        element.classList.add('coffret-slot--pending');
        element.dataset.slotId = this.draft.pending.slotId || '';
        element.textContent = 'En attente';
      } else {
        // Premier slot vide = slot en cours de choix → clignotement sobre
        if (index === this.draft.slots.length && step === 'compose') {
          element.classList.add('coffret-slot--active');
        }
        element.textContent = `${index + 1}`;
      }
      list.appendChild(element);
    }

    this.refs['slots-count'].textContent = this.bf
      ? `${this.draft.slots.length} paire${this.draft.slots.length > 1 ? 's' : ''} sur ${this.draft.capacity}`
      : `${this.draft.slots.length}/${this.draft.capacity} paires`;
    this.refs['slots-continue'].hidden = step !== 'compose';
    this.refs['slots-continue'].disabled = !this.#packIsValid();
    // 3 paires → étape emballage ; 2/5 → le bouton ajoute directement.
    if (!this.bf) this.refs['slots-continue'].textContent = this.#packSize() === 3 ? 'Continuer' : 'Ajouter le pack au panier';
    if (this.refs['slots-back']) this.refs['slots-back'].hidden = step !== 'finish';
  }

  /** Pastille du slot : taille + gamme (logotype pour Race). */
  #slotMetaHtml(slot) {
    const taille = this.#escape((slot.taille || '').split(' ')[0]);
    const isRace = (slot.gamme || '').toLowerCase() === 'race';
    const logoUrl = this.dataset.raceLogo;
    const gamme = isRace && logoUrl
      ? `<img class="gamme-logo" src="${this.#escape(logoUrl)}" alt="Race">`
      : this.#escape(slot.gamme || '');
    return [taille, gamme].filter(Boolean).join(' · ') || this.#escape(slot.title.slice(0, 10));
  }

  #renderPackaging() {
    const container = this.refs['packaging-choices'];
    if (!container) return;

    // Capacités 2 et 5 : aucun coffret physique disponible → étape masquée,
    // emballage forcé à « standard sans coffret » (couvre aussi les brouillons
    // sauvegardés avant cette règle).
    const stepBlock = this.refs['packaging-step'];
    if (this.#packSize() !== 3) {
      if (this.draft.packaging.type !== 'none') {
        this.draft.packaging = { type: 'none', variantId: null, price: 0 };
      }
      if (stepBlock) stepBlock.hidden = true;
      container.innerHTML = '';
      return;
    }
    if (stepBlock) stepBlock.hidden = false;

    const capacityLabel = `${this.#packSize()} paires`;
    const byGamme = (gamme) =>
      this.config.packaging.variants.find(
        (variant) => variant.options.includes(capacityLabel) && variant.options.includes(gamme) && variant.available,
      );

    const choices = [
      { type: 'none', label: 'Pas de coffret', sub: 'Emballage standard', price: 0, variantId: null, image: null },
    ];
    const standard = byGamme('Standard');
    if (standard) choices.push({ type: 'standard', label: 'Coffret cadeau', sub: 'La boîte signature', price: standard.price, variantId: standard.id, image: standard.image });
    const premium = byGamme('Premium');
    if (premium) choices.push({ type: 'premium', label: 'Coffret premium', sub: 'Finition luxe', price: premium.price, variantId: premium.id, image: premium.image });

    container.innerHTML = choices
      .map(
        (choice) => `
        <button type="button" class="perso-qty-card coffret-packaging ${this.draft.packaging.type === choice.type ? 'is-selected' : ''}"
                data-packaging-type="${choice.type}" data-variant-id="${choice.variantId ?? ''}" data-price="${choice.price}">
          ${choice.image ? `<img class="coffret-capacity__img" src="${this.#escape(choice.image)}" alt="${this.#escape(choice.label)}" loading="lazy">` : '<span class="coffret-packaging__placeholder">📦</span>'}
          <span class="perso-qty-card__title">${choice.label}</span>
          <span class="perso-qty-card__subtitle">${choice.sub}</span>
          <span class="perso-qty-card__price">${choice.price > 0 ? '+ ' + formatMoney(choice.price) : ''}</span>
        </button>`,
      )
      .join('');
  }

  #renderSummary() {
    const summary = this.refs['summary'];
    if (!summary) return;

    const pairsTotal = this.draft.slots.reduce((sum, slot) => sum + slot.price, 0);
    // Offre Back To School (config.bogo) : à 5 paires, la moins chère offerte
    // ET -15 % sur le reste ; dès 3 paires, la moins chère offerte — même
    // règle que le backend, qui remplace le pourcentage.
    let discount, discountLabel;
    if (this.config.bogo && this.draft.slots.length >= 5) {
      const cheapest = Math.min(...this.draft.slots.map((slot) => slot.price));
      discount = cheapest + Math.round(((pairsTotal - cheapest) * 15) / 100);
      discountLabel = '🎁 4+1 offerte (-15%)';
    } else if (this.config.bogo && this.draft.slots.length >= 3) {
      const freeCount = Math.floor(this.draft.slots.length / 3);
      discount = this.draft.slots
        .map((slot) => slot.price)
        .sort((a, b) => a - b)
        .slice(0, freeCount)
        .reduce((sum, price) => sum + price, 0);
      discountLabel = freeCount > 1 ? `🎁 ${freeCount} paires offertes` : '🎁 1 paire offerte';
    } else {
      const percent = this.#discountPercent();
      discount = Math.round((pairsTotal * percent) / 100);
      discountLabel = this.config.offerLabel ? `${this.config.offerLabel} (-${percent}%)` : `Remise coffret (-${percent}%)`;
    }
    const packagingPrice = this.draft.packaging.type && this.draft.packaging.type !== 'none' ? this.draft.packaging.price : 0;
    const total = pairsTotal - discount + packagingPrice;

    const lines = [
      `<div class="coffret-composer__summary-line"><span>${this.draft.slots.length} paires</span><span>${formatMoney(pairsTotal)}</span></div>`,
    ];
    if (discount > 0) {
      lines.push(
        `<div class="coffret-composer__summary-line coffret-composer__summary-line--discount"><span>${discountLabel}</span><span>- ${formatMoney(discount)}</span></div>`,
      );
    }
    if (packagingPrice) lines.push(`<div class="coffret-composer__summary-line"><span>Emballage</span><span>${formatMoney(packagingPrice)}</span></div>`);
    lines.push(`<div class="coffret-composer__summary-line coffret-composer__summary-line--total"><span>Total</span><span>${formatMoney(total)}</span></div>`);
    lines.push('<p class="coffret-composer__summary-note">La remise est appliquée automatiquement au panier.</p>');
    summary.innerHTML = lines.join('');

    this.refs['add-coffret'].disabled = !this.#packIsValid() || !this.draft.packaging.type;
  }

  // ——————————————————————————————————————————————— panier ———

  async #addToCart() {
    // L'ajout peut partir du bouton finish (3 paires) OU du bouton de la barre
    // de slots (2/5 paires, ajout direct) : on neutralise les deux.
    const button = this.refs['add-coffret'];
    const continueButton = this.refs['slots-continue'];
    button.disabled = true;
    if (continueButton) continueButton.disabled = true;
    this.refs['composer-error'].hidden = true;

    const shared = {
      _coffretId: this.draft.coffretId,
      _coffretCapacity: String(this.#packSize()),
      // Barème spécial (Black Friday public / VIP) + page d'origine pour
      // « Modifier » depuis le panier
      ...(this.config.offer ? { _coffretOffer: this.config.offer } : {}),
      ...(this.config.page ? { _coffretPage: this.config.page } : {}),
    };

    const items = this.draft.slots.map((slot) => ({
      id: slot.variantId,
      quantity: 1,
      properties: {
        ...(slot.designId ? { designId: slot.designId } : {}),
        ...shared,
        _coffretRole: 'paire',
      },
    }));

    if (this.draft.packaging.type && this.draft.packaging.type !== 'none' && this.draft.packaging.variantId) {
      items.push({ id: this.draft.packaging.variantId, quantity: 1, properties: { ...shared, _coffretRole: 'emballage' } });
    }

    const sections = [];
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.dataset.sectionId) sections.push(drawer.dataset.sectionId);

    try {
      // Mode édition (ou double ajout du même coffret) : les anciennes lignes
      // portant ce coffretId sont retirées avant le ré-ajout — remplacement net
      const currentCart = await fetch('/cart.js').then((r) => r.json());
      const stale = (currentCart.items || []).filter(
        (item) => item.properties?._coffretId === this.draft.coffretId,
      );
      for (const item of stale) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Une des paires est indisponible.');
      }

      const cart = await fetch('/cart.js').then((r) => r.json());

      try {
        localStorage.removeItem(this.draftKey);
      } catch {}
      this.draft = this.#freshDraft();

      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: cart.item_count, source: 'coffret-composer', sections: payload.sections }),
      );
      if (this.#packagingDialogOpen()) this.refs['packaging-dialog'].closest('dialog-component')?.close();
      document.querySelector('[data-ref="cart-drawer"]')?.open?.();
      this.#render();
    } catch (error) {
      this.refs['composer-error'].textContent = `Impossible d'ajouter le pack : ${error.message}`;
      this.refs['composer-error'].hidden = false;
      // En ajout direct (2/5 paires) le panneau d'erreur du finish est masqué :
      // on remonte l'erreur de façon visible.
      if (this.draft.step !== 'finish' && !this.#packagingDialogOpen()) alert(`Impossible d'ajouter le pack : ${error.message}`);
      button.disabled = false;
      if (continueButton) continueButton.disabled = false;
    }
  }

  #escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }
}

if (!customElements.get('coffret-composer')) {
  customElements.define('coffret-composer', CoffretComposer);
}
